import upperFirst from "lodash/upperFirst";
import React from "react";
import "react-datepicker/dist/react-datepicker.css";

import InfoIcon from "./assets/info2.svg?react";
import QuestionIcon from "./assets/question.svg?react";
import { useEventSubscriber, useEventPublisher } from "@/lib/useEventEmitter";
import PartModal, { type PartModalRef } from "@/PartModal";
import PartPendingActionWrapper from "@/PartPendingActionWrapper";
import formStyles from "@/styles/form.module.scss";
import { EventMap } from "@/types/event-types";

import styles from "./ModalQuestion.module.scss";

const QuestionConfirm: React.FC<
  EventMap["questionRequest"][0] & {
    closeModal: () => void;
  }
> = ({ closeModal, ...props }) => {
  return (
    <form
      className={formStyles.form}
      onReset={() => {
        closeModal();
      }}
      autoComplete="off"
    >
      <div>{/* something empty for apply margin in formActionButtonsContainer */}</div>
      <div className={formStyles.formActionButtonsContainer}>
        {props.variants.map(v => (
          <PartPendingActionWrapper
            key={v.slug}
            block
            onClick={async () => {
              await v.action?.();
              closeModal();
            }}
          >
            <button
              key={v.slug}
              type="button"
              className={
                (v.variant && formStyles["button" + upperFirst(v.variant)]) ||
                formStyles.buttonDefault
              }
              data-test-element-type="button"
              data-test-action-type={v.slug?.toLowerCase()}
            >
              {v.title}
            </button>
          </PartPendingActionWrapper>
        ))}
      </div>
    </form>
  );
};

const ModalQuestion = React.forwardRef<PartModalRef>((_, ref) => {
  const [questionProps, setQuestionProps] = React.useState<
    EventMap["questionRequest"][0] | undefined
  >();

  const localRef = React.useRef<PartModalRef | null>(null);

  React.useImperativeHandle(ref, () => localRef.current!, []);

  useEventSubscriber("questionRequest", (options: EventMap["questionRequest"][0]) => {
    setQuestionProps(options);

    localRef.current?.show();
  });

  return (
    <PartModal
      ref={localRef}
      noHeader
      afterClose={() => setQuestionProps(undefined)}
      dontCloseOnEscapePress
    >
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          {questionProps?.iconVariant === "info" ? <InfoIcon /> : <QuestionIcon />}
        </div>
        <span>{questionProps?.title || "Question"}</span>
      </div>
      <div className={styles.description}>{questionProps?.message}</div>
      {questionProps !== undefined ? (
        <QuestionConfirm
          {...questionProps}
          closeModal={() => {
            localRef.current?.hide();
          }}
        />
      ) : null}
    </PartModal>
  );
});

export const useModalQuestion = () => {
  const publish = useEventPublisher();

  return React.useCallback(
    (options: EventMap["questionRequest"][0]) => {
      publish("questionRequest", options);
    },
    [publish],
  );
};

ModalQuestion.displayName = "ModalQuestion";

export default ModalQuestion;
